/**
 * Doprava + platba (Shoptet `billingAndShipping`) — port z `old-js/modules/orderProcess/billingShipping.js`.
 * Načítá se z globálního `scripts.js` (footer). Bez jQuery; bez „robotické přepravy“ z `orderProcess/main.js`.
 */

import {
  getCartGoodsType,
  getCartLines,
  normalizePageTypeName,
  safeGetShoptetDataLayer,
  skuStartsWithAny,
} from './checkout-helpers.js';

/** @param {Record<string, unknown>} cfg */
function log(cfg, ...args) {
  if (cfg?.debug !== true) return;
  // eslint-disable-next-line no-console
  console.log('[YG checkout]', ...args);
}

function cartContainsCrossdock(cfg, dl) {
  const prefixes = cfg.classification?.crossdockPrefixes ?? ['FYZCRS'];
  for (const item of getCartLines(dl)) {
    const code = item?.code;
    if (typeof code !== 'string') continue;
    if (skuStartsWithAny(code, prefixes)) return true;
  }
  return false;
}

function pageIsBillingAndShipping() {
  const pt = safeGetShoptetDataLayer()?.pageType;
  if (typeof pt !== 'string') return false;
  const n = normalizePageTypeName(pt);
  return n === 'billingandshipping' || pt === 'billingAndShipping';
}

function setActiveShippingAndPaymentsFromShoptet() {
  try {
    window.shoptet?.checkoutShared?.setActiveShippingAndPayments?.();
  } catch (e) {
    // ignore
  }
}

function shippingRows() {
  const wrap = document.getElementById('order-shipping-methods');
  if (!wrap) return [];
  const byAttr = wrap.querySelectorAll('[data-id^="shipping-"]');
  if (byAttr.length) return Array.from(byAttr);
  return Array.from(wrap.querySelectorAll('input[name="shippingId"]'))
    .map(inp => inp.closest('.radio-wrapper'))
    .filter(/** @returns {el is Element} */ el => el instanceof Element && wrap.contains(el));
}

function billingRows() {
  return Array.from(document.querySelectorAll('#order-billing-methods [data-id^="billing-"]'));
}

/** Počet přímých potomků jako starý `$("#order-shipping-methods > div").length` */
function shippingDirectChildrenCount() {
  return document.querySelectorAll('#order-shipping-methods > div').length;
}

/** @param {number[]} ids */
function atLeastOneShippingExists(ids) {
  const wrap = document.getElementById('order-shipping-methods');
  if (!wrap || !Array.isArray(ids) || !ids.length) return false;
  return ids.some(id => {
    if (wrap.querySelector(`[data-id="shipping-${id}"]`)) return true;
    return !!wrap.querySelector(`input[name="shippingId"][value="${id}"]`);
  });
}

/** @param {number} shippingId */
function removeSpecificShippingMethod(shippingId) {
  const wrap = document.getElementById('order-shipping-methods');
  if (!wrap) return;
  const byId = wrap.querySelector(`[data-id="shipping-${shippingId}"]`);
  if (byId) {
    byId.remove();
    return;
  }
  const input = wrap.querySelector(`input[name="shippingId"][value="${shippingId}"]`);
  const row = input?.closest('.radio-wrapper') ?? input?.closest('div');
  if (row && wrap.contains(row)) row.remove();
}

/**
 * @param {boolean} deleteDig — true = z košíku není čistě digitální → mažou se „digitální“ dopravy
 */
function parseShippingIdFromRow(row) {
  const idAttr = row.getAttribute('data-id');
  if (idAttr?.startsWith('shipping-')) {
    const n = Number.parseInt(idAttr.replace('shipping-', '').trim(), 10);
    if (Number.isFinite(n)) return n;
  }
  const inp = row.querySelector('input[name="shippingId"]');
  if (inp instanceof HTMLInputElement && inp.value) {
    const n = Number.parseInt(inp.value, 10);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function removeShippingMethods(cfg, bs, deleteDig) {
  const raw = bs.digitalDeliveryIDs ?? bs.digitalDeliveryIds ?? [];
  const digitalIds = Array.isArray(raw)
    ? raw.map(x => Number(x)).filter(n => Number.isFinite(n))
    : [];
  if (!digitalIds.length) return;

  const guardEnter =
    (!deleteDig &&
      shippingDirectChildrenCount() > digitalIds.length &&
      atLeastOneShippingExists(digitalIds)) ||
    deleteDig;

  if (!guardEnter) return;

  /** Snapshot ID před mazáním (live NodeList by se měnil). */
  const toRemove = [];
  for (const row of shippingRows()) {
    const shippingId = parseShippingIdFromRow(row);
    if (shippingId == null) continue;
    const isDigitalRow = digitalIds.includes(shippingId);
    if ((deleteDig && isDigitalRow) || (!deleteDig && !isDigitalRow)) {
      toRemove.push(shippingId);
    }
  }
  for (const id of toRemove) removeSpecificShippingMethod(id);

  setDefaultDeliveryMethod();
}

function setDefaultDeliveryMethod() {
  const wrap = document.getElementById('order-shipping-methods');
  if (!wrap) return;

  const active = wrap.querySelector('.radio-wrapper.active') || wrap.querySelector('[data-id^="shipping-"].active');
  let target =
    active ||
    wrap.querySelector(':scope > [data-id^="shipping-"]') ||
    wrap.querySelector('[data-id^="shipping-"]');
  if (!target) return;

  const input = target.querySelector('input[name="shippingId"]');
  if (input instanceof HTMLInputElement) input.checked = true;
  target.classList.add('active');
  shippingRows().forEach(el => {
    if (el !== target) el.classList.remove('active');
  });
  setActiveShippingAndPaymentsFromShoptet();
}

/** @param {number} id */
function removePaymentMethod(id) {
  document.querySelector(`#order-billing-methods [data-id="billing-${id}"]`)?.remove();
}

/**
 * @param {Record<string, unknown>} cfg
 * @param {{ productIDs?: string[]; productIds?: string[]; allowedBillingIDs?: number[]; allowedBillingIds?: number[] }} cpl
 */
function cardsPaymentLimitRemove(cfg, cpl) {
  if (!pageIsBillingAndShipping()) return;

  const productIds = cpl.productIDs ?? cpl.productIds;
  const allowedRaw = cpl.allowedBillingIDs ?? cpl.allowedBillingIds;
  if (!Array.isArray(productIds) || !Array.isArray(allowedRaw) || !allowedRaw.length) return;

  const allowed = allowedRaw.map(n => Number(n)).filter(Number.isFinite);

  const cart = getCartLines(safeGetShoptetDataLayer());
  let hit = false;
  for (const item of cart) {
    if (typeof item?.code === 'string' && productIds.includes(item.code)) {
      hit = true;
      break;
    }
  }
  if (!hit) return;

  const wrap = document.getElementById('order-billing-methods');
  if (!wrap) return;

  for (const row of billingRows()) {
    const idAttr = row.getAttribute('data-id');
    if (!idAttr?.startsWith('billing-')) continue;
    const pid = Number(idAttr.replace('billing-', ''));
    if (!Number.isFinite(pid)) continue;
    if (!allowed.includes(pid)) row.remove();
  }

  const active = wrap.querySelector('.radio-wrapper.active') || wrap.querySelector('[data-id^="billing-"].active');
  if (!active) {
    const first = wrap.querySelector('[data-id^="billing-"]');
    const clickable = first?.querySelector('label') || first?.querySelector('input');
    if (clickable instanceof HTMLElement) clickable.click();
  }
}

function removeCODForCrossdock(cfg, bs) {
  const dl = safeGetShoptetDataLayer();
  if (!cartContainsCrossdock(cfg, dl)) return;

  const ids = bs.removeBillingForCrossdock ?? bs.removeBillingIdsForCrossdock;
  if (!Array.isArray(ids)) return;
  for (const id of ids) removePaymentMethod(Number(id));
}

/**
 * @param {Record<string, unknown>} cfg
 * @param {Record<string, unknown>} rec
 */
function makeBillingShippingHighlights(cfg, rec) {
  const shipWrap = document.getElementById('order-shipping-methods');
  const payWrap = document.getElementById('order-billing-methods');
  if (!shipWrap || !payWrap) return;

  const shippingRec = rec.shippingRecIDs ?? rec.shippingRecommendedTextById;
  const shippingTom = rec.shippingTomIDs ?? rec.shippingTomorrowIconIds;
  const tomTip = rec.shippingTomTooltipText ?? rec.shippingTomorrowTooltip ?? '';
  const paymentRec = rec.paymentRecIDs ?? rec.paymentRecommendedTextById;

  if (shippingRec && typeof shippingRec === 'object') {
    for (const row of shippingRows()) {
      const idAttr = row.getAttribute('data-id');
      const sid = Number(idAttr?.replace('shipping-', ''));
      if (!Number.isFinite(sid)) continue;
      if (!Object.prototype.hasOwnProperty.call(shippingRec, sid)) continue;
      const text = /** @type {Record<string, string>} */ (shippingRec)[String(sid)];
      const info = row.querySelector('label .payment-info');
      const b = info?.querySelector('b');
      if (b?.parentElement && text) {
        const badge = document.createElement('i');
        badge.className = 'yg-highlighted-badge';
        badge.textContent = text;
        b.after(badge);
      }
    }
  }

  if (Array.isArray(shippingTom) && shippingTom.length) {
    for (const row of shippingRows()) {
      const idAttr = row.getAttribute('data-id');
      const sid = Number(idAttr?.replace('shipping-', ''));
      if (!Number.isFinite(sid) || !shippingTom.includes(sid)) continue;
      const info = row.querySelector('label .payment-info');
      const b = info?.querySelector('b');
      if (b?.parentElement) {
        const icon = document.createElement('i');
        icon.className = 'deliveryTommorow-icon show-tooltip';
        if (tomTip) icon.setAttribute('title', tomTip);
        b.after(icon);
      }
    }
  }

  if (paymentRec && typeof paymentRec === 'object') {
    for (const row of billingRows()) {
      const idAttr = row.getAttribute('data-id');
      const pid = Number(idAttr?.replace('billing-', ''));
      if (!Number.isFinite(pid)) continue;
      if (!Object.prototype.hasOwnProperty.call(paymentRec, pid)) continue;
      const text = /** @type {Record<string, string>} */ (paymentRec)[String(pid)];
      const info = row.querySelector('label .payment-info');
      const b = info?.querySelector('b');
      if (b?.parentElement && text) {
        const badge = document.createElement('i');
        badge.className = 'yg-highlighted-badge';
        badge.textContent = text;
        b.after(badge);
      }
    }
  }

  applyPriceDiscountLabels();
}

/** Sleva u ceny + rozbalení seznamů (lze volat i bez `checkoutRecommendations`). */
function applyPriceDiscountLabels() {
  for (const row of document.querySelectorAll('.shipping-billing-table .radio-wrapper, #order-shipping-methods .radio-wrapper, #order-billing-methods .radio-wrapper')) {
    const prEl = row.querySelector('.payment-shipping-price');
    if (!prEl) continue;
    const raw =
      prEl.getAttribute('data-shipping-price') ?? prEl.getAttribute('data-billing-price');
    if (raw == null) continue;
    const pr = Number(raw);
    if (pr < 0 && !prEl.querySelector('.discount')) {
      const inner = document.createElement('span');
      inner.className = 'discount';
      inner.textContent = prEl.textContent?.trim() ?? '';
      prEl.replaceChildren(inner);
    }
  }
}

let toggleDelegated = false;

/**
 * Položky pro rozbalení: nejdřív přímí potomci (jako starý `#order-shipping-methods > div`),
 * jinak všechny `.radio-wrapper` uvnitř wrap (novější šablony mají mezi vrstvou obal).
 */
function radioWrappersForPartition(wrap) {
  const direct = [...wrap.querySelectorAll(':scope > .radio-wrapper')];
  if (direct.length > 2) return direct;
  const flat = [...wrap.querySelectorAll('.radio-wrapper')];
  if (flat.length > 2) return flat;
  return direct;
}

/**
 * Digitální košík: skryje bloky ve formuláři objednávky (např. plugin data doručení FV).
 * Port `old-js/modules/goodsDiversification.hideDateOfDeliveryPlugin` (původně jen `.co-box.fv-datum`).
 */
function hideOrderFormBlocksForDigitalCart(cfg) {
  if (!pageIsBillingAndShipping()) return;
  if (getCartGoodsType(cfg, safeGetShoptetDataLayer()) !== 1) return;
  const raw = cfg.checkout?.hideSelectorsWhenCartIsDigital;
  const selectors =
    Array.isArray(raw) && raw.length > 0 ? raw : ['#order-form .co-box.fv-datum'];
  for (const sel of selectors) {
    try {
      document.querySelectorAll(sel).forEach(el => {
        if (el instanceof HTMLElement) el.style.display = 'none';
      });
    } catch {
      /* neplatný selektor */
    }
  }
}

/** Rozbalení „více možností“ (>2 položky) — stejná idea jako starý kód */
function partitionShippingPaymentList(wrap) {
  if (wrap.dataset.ygPartitioned === '1') return;
  const radios = radioWrappersForPartition(wrap);
  if (radios.length <= 2) return;

  const [a, b, ...rest] = radios;
  const initial = document.createElement('div');
  initial.className = 'yg-shipping-payments-intial-options';
  const extra = document.createElement('div');
  extra.className = 'yg-shipping-payments-extra-options';

  a.before(initial);
  initial.append(a, b);
  rest.forEach(el => extra.appendChild(el));
  initial.after(extra);

  const toggle = document.createElement('a');
  toggle.href = '#vice-moznosti';
  toggle.className = 'yg-shipping-payments-toggle';
  toggle.innerHTML =
    '<span class="c">Zobrazit více možností</span><span class="o">Zobrazit méně možností</span><i></i>';
  wrap.append(toggle);

  wrap.dataset.ygPartitioned = '1';

  if (!toggleDelegated) {
    toggleDelegated = true;
    document.addEventListener('click', e => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const link = t.closest('.yg-shipping-payments-toggle');
      if (!link) return;
      e.preventDefault();
      const parent = link.parentElement;
      if (parent) parent.classList.toggle('yg-shipping-payments-extra-options-opened');
    });
  }
}

/** Jedna aplikace všech úprav */
export function applyBillingShippingCheckout(cfg) {
  if (!pageIsBillingAndShipping()) return;

  const checkout = cfg.checkout ?? {};
  const bs =
    checkout.orderProcess_billingShipping ??
    checkout.billingShipping ??
    {};

  if (bs.enabled === false) return;

  const dl = safeGetShoptetDataLayer();
  const goodsType = getCartGoodsType(cfg, dl);
  const deleteDig = goodsType !== 1;

  removeShippingMethods(cfg, bs, deleteDig);

  const cpl = checkout.cardsPaymentLimit ?? cfg.cardsPaymentLimit;
  if (cpl && typeof cpl === 'object') cardsPaymentLimitRemove(cfg, /** @type {*} */ (cpl));

  removeCODForCrossdock(cfg, bs);

  const rec = bs.checkoutRecommendations;
  if (rec && typeof rec === 'object') {
    makeBillingShippingHighlights(cfg, /** @type {Record<string, unknown>} */ (rec));
  } else {
    applyPriceDiscountLabels();
  }

  if (bs.expandShippingPaymentLists !== false) {
    const shipWrap = document.getElementById('order-shipping-methods');
    const payWrap = document.getElementById('order-billing-methods');
    if (shipWrap) partitionShippingPaymentList(shipWrap);
    if (payWrap) partitionShippingPaymentList(payWrap);
  }

  hideOrderFormBlocksForDigitalCart(cfg);

  log(cfg, 'billingShipping applied', {
    goodsType,
    deleteDig,
    digitalIds: (bs.digitalDeliveryIDs ?? bs.digitalDeliveryIds ?? []).map(x => Number(x)).filter(Number.isFinite),
    pageType: safeGetShoptetDataLayer()?.pageType,
  });
}

let installScheduled = false;

/**
 * @param {Record<string, unknown> | (() => Record<string, unknown>)} cfgOrGetter
 *        Getter se doporučuje — vždy aktuální `window.YG_CONFIG` (pořadí skriptů v Shoptetu).
 */
export function installCheckoutBillingShipping(cfgOrGetter) {
  const getCfg = typeof cfgOrGetter === 'function' ? cfgOrGetter : () => cfgOrGetter;
  const cfg0 = getCfg();
  const checkout0 = cfg0.checkout ?? {};
  const bs0 =
    checkout0.orderProcess_billingShipping ??
    checkout0.billingShipping ??
    {};
  if (bs0.enabled === false) return;

  const run = () => {
    const cfg = getCfg();
    if (!pageIsBillingAndShipping()) {
      if (cfg.debug === true && document.getElementById('order-shipping-methods')) {
        log(cfg, 'skip: pageType is not billing/shipping (zkontroluj getShoptetDataLayer().pageType)', {
          pageType: safeGetShoptetDataLayer()?.pageType,
        });
      }
      return;
    }
    try {
      applyBillingShippingCheckout(cfg);
    } catch (e) {
      log(cfg, 'applyBillingShippingCheckout error', e);
    }
  };

  document.addEventListener('ShoptetDOMContentLoaded', run);
  /** Shoptet někdy přerenderuje krok později */
  if (!installScheduled) {
    installScheduled = true;
    document.addEventListener('ShoptetCartUpdated', run);
    setTimeout(run, 400);
    setTimeout(run, 1200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
}
